import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Bell, TrendUp, TrendDown, Trash } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function PriceAlertModal({ open, item, onClose, onSaved }) {
  const [direction, setDirection] = useState(item?.alert_direction || "above");
  const [price, setPrice] = useState(item?.alert_price ? String(item.alert_price) : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    setDirection(item?.alert_direction || "above");
    setPrice(item?.alert_price ? String(item.alert_price) : "");
    setErr("");
  }, [item]);

  if (!open || !item) return null;
  const sym = (item.symbol || "").replace(".NS", "");
  const ltp = item.ltp;

  const submit = async (e) => {
    e.preventDefault();
    const p = parseFloat(price);
    if (!p || p <= 0) {
      setErr("Enter a valid price");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.put(`/watchlist/${item.symbol}/alert`, {
        price: p,
        direction,
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to set alert");
    } finally {
      setBusy(false);
    }
  };

  const clearAlert = async () => {
    setBusy(true);
    try {
      await api.put(`/watchlist/${item.symbol}/alert`, { price: null });
      onSaved?.();
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm flex items-center justify-center p-6"
        onClick={onClose}
        data-testid="price-alert-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-surface border border-pos/40 rounded-sm p-6 shadow-2xl"
        >
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-sm bg-pos-soft border border-pos-soft flex items-center justify-center">
                <Bell size={16} weight="fill" className="text-pos" />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-pos">
                  /// Price alert
                </div>
                <div className="font-mono text-base font-semibold text-primary-fg">{sym}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="alert-close"
              className="p-1 text-muted-fg hover:text-primary-fg rounded-sm"
            >
              <X size={16} />
            </button>
          </div>

          {ltp != null && (
            <div className="mb-4 px-3 py-2 bg-surface-hover rounded-sm font-mono text-xs flex items-center justify-between">
              <span className="text-muted-fg uppercase tracking-widest text-[10px]">Live LTP</span>
              <span className="text-primary-fg tabular-nums">₹{ltp.toFixed(2)}</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-2">
                Trigger when price goes
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="alert-dir-above"
                  onClick={() => setDirection("above")}
                  className={
                    "py-2.5 rounded-sm text-xs font-mono uppercase tracking-widest border flex items-center justify-center gap-1.5 transition " +
                    (direction === "above"
                      ? "bg-pos-soft border-pos text-pos"
                      : "border-subtle text-muted-fg hover:text-primary-fg")
                  }
                >
                  <TrendUp size={13} weight="bold" /> Above
                </button>
                <button
                  type="button"
                  data-testid="alert-dir-below"
                  onClick={() => setDirection("below")}
                  className={
                    "py-2.5 rounded-sm text-xs font-mono uppercase tracking-widest border flex items-center justify-center gap-1.5 transition " +
                    (direction === "below"
                      ? "bg-mod-soft border-mod text-mod"
                      : "border-subtle text-muted-fg hover:text-primary-fg")
                  }
                >
                  <TrendDown size={13} weight="bold" /> Below
                </button>
              </div>
            </div>

            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
                Price level (₹)
              </span>
              <input
                data-testid="alert-price-input"
                type="number"
                step="0.05"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
                required
                className="w-full mt-1 px-3 py-2.5 bg-app border border-subtle rounded-sm text-primary-fg font-mono text-sm focus:border-focus transition tabular-nums"
              />
            </label>

            {err && (
              <div
                data-testid="alert-error"
                className="text-neg text-xs bg-neg-soft border border-neg-soft px-3 py-2 rounded-sm"
              >
                {err}
              </div>
            )}

            <div className="text-[11px] text-muted-fg leading-relaxed">
              One-shot alert · fires once when crossed during NSE market hours, then auto-clears.
              Email reaches you only if email-alerts are ON in the top bar.
            </div>

            <div className="flex items-center gap-2 pt-2">
              {item.alert_price && (
                <button
                  type="button"
                  onClick={clearAlert}
                  disabled={busy}
                  data-testid="alert-clear"
                  className="px-3 py-2.5 text-xs font-mono uppercase tracking-widest text-neg hover:bg-neg-soft border border-neg-soft rounded-sm flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Trash size={12} weight="bold" /> Clear
                </button>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="alert-save"
                className="ml-auto px-5 py-2.5 rounded-sm text-xs font-medium disabled:opacity-50"
                style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
              >
                {busy ? "Saving…" : item.alert_price ? "Update alert" : "Set alert"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
